import { PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { useCrtVersionSelectionSync } from '@/hooks/crt/useCrtVersionSelectionSync'
import {
  customerInvestmentsSearchSchema,
  useCustomerInvestments,
} from '@/hooks/queries/crt/useCustomerInvestments'
import { useTablePagination } from '@/hooks/useTablePagination'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toAssumptionBadges } from '../../-components/assumptions/assumption-selection'
import { useOpenCrtConfigure } from '@/hooks/crt/useOpenCrtConfigure'
import { InvestmentsDataTable } from './-components/InvestmentsDataTable'

const ASSUMPTION_KINDS = ['manual'] as const

export const Route = createFileRoute('/_auth/_modules/crt/customer-investments/investments/')({
  component: RouteComponent,
  validateSearch: (search) => customerInvestmentsSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { crtPeriod } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
    fields: ['manualInvestmentIds'],
  })
  // What the page applies is changed from the header's configure dialog, which its badges open.
  const openConfigure = useOpenCrtConfigure()
  const configuration = { manualInvestmentIds: search.manualInvestmentIds }
  const configBadges = toAssumptionBadges(t, ASSUMPTION_KINDS, configuration)

  const { loadedVersionId: _loadedVersionId, ...dataSearch } = search
  const queries = {
    ...dataSearch,
    crtPeriod,
    page: search.page ?? 1,
    pageSize,
  }

  const { isPending, error, data } = useCustomerInvestments(queries)

  return (
    <PageContainer maxWidth="wide">
      <Spread>
        <PageHeader>{t('investments.pageHeader')}</PageHeader>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <InvestmentsDataTable
            data={data?.results ?? []}
            rowCount={data?.rowCount ?? 0}
            totals={data?.totals}
            isPending={isPending}
            error={error}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            configBadges={configBadges}
            onConfigBadgeClick={openConfigure}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
